import { Button, DatePicker, Flex, Form, Input, Select } from "antd"
import { spacings } from "themes/variables"
import { Modal } from "ui/Modal"
import { useTranslation } from "react-i18next"
import { Title } from "ui/Typography"
import { TextArea } from "ui/Input"
import styles from "./index.module.css"
import { useSelector, useDispatch } from "react-redux"
import { useEffect, useState } from "react"
import dayjs from "dayjs"
import {
  getCalendarBookings,
  getCalendarJournalNote,
  getCalendarTripLocations,
  saveCalendarJournal,
  getJournalVersions
} from "store/calendar"
import {
  getCrewMemberJournalNote,
  getCrewMemberTripLocations,
  saveCrewMemberJournal,
  getCrewMemberJournalVersions
} from "store/crewMemberJournal"
import { getCrewMemberCalendar } from "store/crewMemberCalendar"
import {
  getEquipmentJournalNote,
  getEquipmentTripLocations,
  saveEquipmentJournal,
  getEquipmentJournalVersions
} from "store/equipmentJournal"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import PropTypes from "prop-types"
import { toaster } from "components/common/Toast"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import VersionHistory from "./VersionHistory"

const Journal = ({ open, setOpen, onCancel, selectedDate, startDate, endDate, context = "guide", crewMemberId, equipmentId, onSuccess }) => {
  const [form] = Form.useForm()
  const [showHistory, setShowHistory] = useState(false)
  const [versionPage, setVersionPage] = useState(1)
  const [versionPageSize, setVersionPageSize] = useState(25)

  // Select the appropriate Redux store based on context
  const isCrewMemberContext = context === "crew_member"
  const isEquipmentContext = context === "equipment"
  const { journalNote, tripLocations, journalVersions } = useSelector((state) => {
    if (isEquipmentContext) return state.equipmentJournal
    if (isCrewMemberContext) return state.crewMemberJournal
    return state.calendar
  })

  const isEditJournal = Boolean(journalNote?.data?.id)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isMobile } = useDevice()
  const { hasPermission } = usePermissions()

  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)

  const canSaveJournal = isEditJournal ? canEditJournal : canCreateJournal

  const showSaveButton = canSaveJournal

  useEffect(() => {
    if (isEquipmentContext && equipmentId) {
      dispatch(getEquipmentTripLocations())
      dispatch(getEquipmentJournalNote(equipmentId, selectedDate))
    } else if (isCrewMemberContext && crewMemberId) {
      dispatch(getCrewMemberTripLocations())
      dispatch(getCrewMemberJournalNote(crewMemberId, selectedDate))
    } else {
      dispatch(getCalendarTripLocations())
      dispatch(getCalendarJournalNote(selectedDate))
    }
  }, [selectedDate, dispatch, isCrewMemberContext, crewMemberId, isEquipmentContext, equipmentId])

  useEffect(() => {
    if (journalNote?.data) {
      const { date, title, body, location } = journalNote.data || {}
      form.setFieldsValue({
        date: selectedDate ? dayjs(selectedDate) : dayjs(date),
        title,
        body,
        location
      })
    } else {
      form.setFieldsValue({
        date: selectedDate ? dayjs(selectedDate) : dayjs()
      })
    }
  }, [journalNote?.data, selectedDate, form])

  const handleFinish = (values) => {
    const formData = {
      ...values,
      date: selectedDate
    }

    if (isEquipmentContext && equipmentId) {
      dispatch(saveEquipmentJournal(equipmentId, journalNote?.data?.id, formData))
        .then((response) => {
          toaster.success(response?.message)
          setOpen(false)
          // Trigger parent component refresh if callback provided
          if (onSuccess) {
            onSuccess()
          }
        })
        .catch(() => {
          // Error handling is done by the api middleware
        })
    } else if (isCrewMemberContext && crewMemberId) {
      dispatch(saveCrewMemberJournal(crewMemberId, journalNote?.data?.id, formData))
        .then((response) => {
          const data = {
            "q[date_gteq]": startDate,
            "q[date_lteq]": endDate
          }
          dispatch(getCrewMemberCalendar(crewMemberId, data))
          toaster.success(response?.message)
        })
        .finally(() => {
          setOpen(false)
        })
    } else {
      dispatch(saveCalendarJournal(journalNote?.data?.id, formData))
        .then((response) => {
          const data = {
            "q[date_gteq]": startDate,
            "q[date_lteq]": endDate
          }
          dispatch(getCalendarBookings(data))
          toaster.success(response?.message)
        })
        .finally(() => {
          setOpen(false)
        })
    }
  }

  const toggleHistoryView = () => {
    const newShowHistory = !showHistory
    setShowHistory(newShowHistory)

    // Fetch version history when showing the history view
    if (newShowHistory && journalNote?.data?.id) {
      if (isEquipmentContext) {
        dispatch(getEquipmentJournalVersions(journalNote.data.id, { page: versionPage, per_page: versionPageSize }))
      } else if (isCrewMemberContext) {
        dispatch(getCrewMemberJournalVersions(journalNote.data.id, { page: versionPage, per_page: versionPageSize }))
      } else {
        dispatch(getJournalVersions(journalNote.data.id, { page: versionPage, per_page: versionPageSize }))
      }
    }
  }

  const handleVersionPageChange = (page) => {
    setVersionPage(page)

    // Fetch new page of version history
    if (journalNote?.data?.id) {
      if (isEquipmentContext) {
        dispatch(getEquipmentJournalVersions(journalNote.data.id, { page, per_page: versionPageSize }))
      } else if (isCrewMemberContext) {
        dispatch(getCrewMemberJournalVersions(journalNote.data.id, { page, per_page: versionPageSize }))
      } else {
        dispatch(getJournalVersions(journalNote.data.id, { page, per_page: versionPageSize }))
      }
    }
  }

  const handleVersionPageSizeChange = (size) => {
    setVersionPageSize(size)
    setVersionPage(1)

    // Fetch version history with new page size
    if (journalNote?.data?.id) {
      if (isEquipmentContext) {
        dispatch(getEquipmentJournalVersions(journalNote.data.id, { page: 1, per_page: size }))
      } else if (isCrewMemberContext) {
        dispatch(getCrewMemberJournalVersions(journalNote.data.id, { page: 1, per_page: size }))
      } else {
        dispatch(getJournalVersions(journalNote.data.id, { page: 1, per_page: size }))
      }
    }
  }

  const modalContent = (
    <FirstLoading loading={journalNote?.loading}>
      <Flex vertical gap={spacings.XL} className={`p-xl ${isMobile ? "p-0" : styles.journalModalContainer}`}>
        {!showHistory ? (
          <>
            <Title level={5}>{t("calendar.journal.journal_details")}</Title>
            <Form layout="vertical" form={form} onFinish={handleFinish}>
              <Form.Item label={t("calendar.journal.date")} className="fw-medium">
                <Form.Item
                  name="date"
                  noStyle
                  rules={[
                    { required: true, message: t("calendar.journal.date_is_required") },
                    () => ({
                      validator(_, value) {
                        if (!value) {
                          return Promise.reject(new Error(t("calendar.journal.date_is_required")))
                        }
                        return Promise.resolve()
                      }
                    })
                  ]}
                >
                  <DatePicker className="w-full" placeholder="Date" format="MM/DD/YYYY" disabled />
                </Form.Item>
              </Form.Item>
              <Form.Item label={t("calendar.journal.title")} className="fw-medium">
                <Form.Item name="title" noStyle>
                  <Input placeholder={t("calendar.journal.placeholders.title")} disabled={!showSaveButton} />
                </Form.Item>
              </Form.Item>
              <Form.Item label={t("calendar.journal.body")} className="fw-medium">
                <Form.Item
                  name="body"
                  noStyle
                  rules={[
                    { required: showSaveButton, message: "Body is required" },
                    () => ({
                      validator(_, value) {
                        if (!showSaveButton || value) {
                          return Promise.resolve()
                        }
                        return Promise.reject(new Error("Body is required"))
                      }
                    })
                  ]}
                >
                  <TextArea disabled={!showSaveButton} />
                </Form.Item>
              </Form.Item>
              <Form.Item label={t("calendar.journal.location")} className="fw-medium">
                <Form.Item name="location" noStyle>
                  <Select
                    placeholder={t("calendar.journal.placeholders.location")}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    disabled={!showSaveButton}
                  >
                    {tripLocations?.data?.map((location) => (
                      <Select.Option key={location} value={location}>
                        {location}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Form.Item>
              <Flex gap={spacings.S} className="mt-3xl">
                <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onCancel}>
                  {t("global.cancel")}
                </Button>
                {showSaveButton && (
                  <Button type="primary" className="py-l px-xl" htmlType="submit">
                    {isEditJournal ? t("calendar.journal.save_changes") : t("global.save")}
                  </Button>
                )}
                 {isEditJournal && (
                  <Button className="py-l px-xl ml-auto" color="default" variant="outlined" onClick={toggleHistoryView}>
                    {t("calendar.journal.view_history")}
                  </Button>
                )}
              </Flex>
            </Form>
          </>
        ) : (
          <>
            <Flex justify="space-between" align="center">
              <Title level={5}>{t("calendar.journal.version_history")}</Title>
              <Button type="link" onClick={toggleHistoryView}>
                {t("calendar.journal.back_to_journal")}
              </Button>
            </Flex>
            <VersionHistory
              versions={journalVersions?.data?.versions}
              loading={journalVersions?.loading}
              pagination={journalVersions?.data?.pagination}
              onPageChange={handleVersionPageChange}
              onPageSizeChange={handleVersionPageSizeChange}
              pageSize={versionPageSize}
            />
          </>
        )}
      </Flex>
    </FirstLoading>
  )

  let journalTitle
  if (isEditJournal) {
    journalTitle = canEditJournal ? t("calendar.journal.edit_journal") : t("calendar.journal.journal_details")
  } else {
    journalTitle = t("calendar.journal.new_journal")
  }

  const modalTitle = journalNote?.loading ? "" : journalTitle

  return isMobile ? (
    <CustomDrawer title={modalTitle} open={open} setOpen={setOpen} onCancel={onCancel} onClose={onCancel}>
      {modalContent}
    </CustomDrawer>
  ) : (
    <Modal centered title={modalTitle} open={open} setOpen={setOpen} onCancel={onCancel} onClose={onCancel}>
      {modalContent}
    </Modal>
  )
}

Journal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  selectedDate: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  context: PropTypes.oneOf(["guide", "crew_member", "equipment"]),
  crewMemberId: PropTypes.string,
  equipmentId: PropTypes.string,
  onSuccess: PropTypes.func
}

export default Journal
